/* eslint-disable react/destructuring-assignment */
import { Fragment, memo, useState, useEffect } from "react";
import ArrowDownIcon from "@Icons/ArrowDownIcon";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import styles from "../../RoomTypeSection.module.scss";
import { unitTypes } from "../../utils";
import unitTypesDescription from "../../utils/unitTypesDescription";
import NoRoomTypesFound from "../NoRoomTypesFound";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsDesktop from "hooks/useIsDesktop";
import RoomTypeCardV2WithSsrData from "../RoomTypeCard/RoomTypeCardV2WithSsrData";

const CustomAccordionItemWithShimmer = (props: any) => {
  const isRoomTypeV2 = true;
  // SSR shimmer placeholder — renders room cards with basic info while CSR loads full data
  const rooms = props?.data || [];

  const [isDescriptionOpen, setisDescriptionOpen] = useState(true);

  useEffect(() => {
    const { hash } = window.location;
    let timerId: ReturnType<typeof setTimeout> | undefined;
    if (hash === "#room_type") {
      timerId = setTimeout(() => {
        const element = document.getElementById("room_type");
        if (element) {
          const headerOffset = 54 + 48; // padding plus navbar height
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 500);
    } else if (hash.startsWith("#room-")) {
      // Scroll to specific room type card (from Video Page "View Room" button)
      timerId = setTimeout(() => {
        const elementId = hash.replace("#", "");
        const element = document.getElementById(elementId);
        if (element) {
          const headerOffset = 54 + 48;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        } else {
          // Fallback: scroll to room_type section if specific room not found (e.g., sold out)
          const fallbackElement = document.getElementById("room_type");
          if (fallbackElement) {
            const headerOffset = 54 + 48;
            const elementPosition = fallbackElement.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });
          }
        }
      }, 500);
    }
    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, []);

  const handleTitleClick = () => {
    gtagEventEmitter({
      action: isDescriptionOpen ? "room_type_collapsed" : "room_type_expanded",
      category: PAGE.INVENTORY,
      tab: props?.tab,
    });
    setisDescriptionOpen(!isDescriptionOpen);
  };
  const isDesktop = useIsDesktop();

  const AccordianItem = () => {
    const { t } = useTranslationHook();
    const unitTypesDesc: any = unitTypesDescription(t);
    return (
      <div
        id={unitTypes[props?.tab]}
        className={`${props.className} ${
          isDescriptionOpen ? "accordion-item active" : "accordion-item"
        } ${formatClassNames(styles.accordionItem, isDesktop && styles.desktop)}`}
        key={props.index}
      >
        <div
          className={formatClassNames(
            styles.accordionHeader,
            props.isDesktop && styles.accordionHeader_desktop,
            isRoomTypeV2 && styles.headerv2,
            isDescriptionOpen && styles.active,
          )}
          onClick={handleTitleClick}
          onKeyDown={handleTitleClick}
          role="none"
          id={`room-type-card-container-${unitTypes[props?.tab]}`}
        >
          <div className={styles.iconContainer}>{unitTypesDesc[props?.tab]?.icon}</div>
          <div className={styles.accordianItemTitleSection}>
            <h3
              data-testid="property-room-type-unit-title"
              className={formatClassNames(styles.accordianItemTitle, isDesktop && styles.desktop)}
            >
              {unitTypes[props?.tab]}
            </h3>
            <div
              data-testid="property-room-type-unit-subtitle"
              className={formatClassNames(
                styles.accordianItemSubTitle,
                props.isDesktop && styles.accordianItemSubTitle_desktop,
              )}
            >
              {t(unitTypesDesc[props?.tab]?.text)}
            </div>
          </div>
          <div className={isDescriptionOpen ? "chevron active" : "chevron"}>
            <ArrowDownIcon />
          </div>
        </div>
        <div
          className={formatClassNames(
            "accordion-content",
            styles.accordionContent,
            props.isDesktop && styles.noPaddingBottom,
          )}
        >
          <>
            {rooms?.map((roomType: any, idx: number) => (
              <Fragment key={idx}>
                {isRoomTypeV2 && (
                  <RoomTypeCardV2WithSsrData
                    id={`room-${roomType?.id}`}
                    key={roomType?.id}
                    data={roomType}
                    isDesktop={props.isDesktop}
                  />
                )}
              </Fragment>
            ))}
            {props.data.length === 0 && <NoRoomTypesFound />}
          </>
        </div>
      </div>
    );
  };

  return <AccordianItem />;
};

export default wrapperHOC(memo(CustomAccordionItemWithShimmer), {
  componentName: "CustomAccordionItem-RoomTypeSection-PropertyPage",
  showForChina: true,
});
